/**
 * The owner's vendor captures, on Owner tools (Idea #416; ADR 0119; on Health
 * until ADR 0198): choose the source, then the window of test calls or
 * sessions. The app builds the sanitized files; download them as a zip to
 * commit under backend/tests/fixtures/. The sources are SkySwitch (ADRs 0025 and 0026),
 * ScreenConnect's pushed events (ADR 0069), its API's answers (ADR 0072),
 * Datto RMM's activity log (ADR 0080) and one tech's mail (roadmap PR-56).
 * The office SonicWall's syslog (ADR 0158) is recorded from now, and can be
 * stopped early while it records.
 *
 * A capture the hygiene scan flagged shows where and why, and can't be
 * downloaded. The API enforces both; this screen only reflects them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useId, useState } from "react";

import { ApiError } from "../../api/client";
import type { Capture, CaptureMailbox, CaptureSource, CaptureStatus } from "../../api/types";
import { useSession } from "../../app/session-context";
import { formatDateTimeIn, zonedToUtc } from "../../lib/dates";
import { saveBlob } from "../../lib/download";

const QUERY_KEY = ["captures"];
const MAILBOXES_KEY = ["captures", "mailboxes"];

const inputClass =
  "h-10 rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

const pillButton =
  "h-9 cursor-pointer rounded-pill border border-line bg-card px-4 text-sm font-medium " +
  "hover:bg-brand-tint disabled:cursor-not-allowed disabled:opacity-50";

const STATUS_LABELS: Record<CaptureStatus, string> = {
  pending: "Preparing…",
  ready: "Ready",
  failed: "Failed",
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

/**
 * Whether a SonicWall capture is still recording: the worker hasn't built it,
 * and its window isn't over. False by itself once the window ends.
 */
function useRecording(capture: Capture): boolean {
  const candidate = capture.source === "sonicwall" && capture.status === "pending";
  const end = Date.parse(capture.window_end);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!candidate) {
      return undefined;
    }
    // At the window's end, which a stop brings forward to now.
    const timer = setTimeout(() => {
      setNow(Date.now());
    }, Math.max(0, end - Date.now()));
    return () => {
      clearTimeout(timer);
    };
  }, [candidate, end]);
  return candidate && now < end;
}

function CaptureRow({
  capture,
  timeZone,
}: {
  capture: Capture;
  timeZone: string;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const recording = useRecording(capture);
  // Stopping early loses nothing (the owner, 2026-10-16): no confirmation.
  const stop = useMutation({
    mutationFn: () => api.stopCapture(capture.id),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  const download = useMutation({
    mutationFn: () => api.downloadCapture(capture.id),
    onSuccess: (blob) => {
      const day = capture.window_start.slice(0, 10);
      saveBlob(blob, `${capture.source}-capture-${day}.zip`);
      setError(null);
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteCapture(capture.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  const totalBytes = capture.files.reduce((sum, f) => sum + f.bytes, 0);

  return (
    <li className="rounded-row border border-line bg-panel-2 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium">
          {`${formatDateTimeIn(capture.window_start, timeZone)} – ${formatDateTimeIn(capture.window_end, timeZone)}`}
        </span>
        <span
          className={
            "rounded-pill px-2 py-0.5 text-xs font-medium " +
            (capture.status === "ready" && capture.findings.length === 0
              ? "bg-status-logged-bg text-status-logged-fg"
              : capture.status === "pending"
                ? "bg-status-in-progress-bg text-status-in-progress-fg"
                : "bg-status-needs-client-bg text-status-needs-client-fg")
          }
        >
          {capture.status === "ready" && capture.findings.length > 0
            ? "Flagged"
            : STATUS_LABELS[capture.status]}
        </span>
        <span className="ml-auto flex gap-2">
          {recording ? (
            <button
              type="button"
              className={pillButton}
              disabled={stop.isPending}
              onClick={() => {
                stop.mutate();
              }}
            >
              Stop recording
            </button>
          ) : null}
          <button
            type="button"
            className={pillButton}
            disabled={!capture.downloadable || download.isPending}
            onClick={() => {
              download.mutate();
            }}
          >
            Download
          </button>
          <button
            type="button"
            className={pillButton}
            disabled={remove.isPending}
            onClick={() => {
              remove.mutate();
            }}
          >
            Delete
          </button>
        </span>
      </div>
      {capture.status === "ready" ? (
        <p className="mt-1 text-sm text-muted">
          {`${String(capture.files.length)} files · ${String(Math.ceil(totalBytes / 1024))} KB`}
        </p>
      ) : null}
      {capture.error ? (
        <p className="mt-1 text-sm text-status-needs-client-fg">
          {capture.error}
        </p>
      ) : null}
      {capture.findings.length > 0 ? (
        <div className="mt-2 text-sm">
          <p className="text-status-needs-client-fg">
            The hygiene scan flagged these, so this capture can&apos;t be
            downloaded. Send Claude the list (it holds no values):
          </p>
          <ul className="mt-1 list-disc pl-5 font-mono text-xs">
            {capture.findings.map((f) => (
              <li
                key={`${f.file}${f.path}`}
              >{`${f.file}: ${f.path}: ${f.reason}`}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {error !== null ? (
        <p role="alert" className="mt-1 text-sm text-status-needs-client-fg">
          {error}
        </p>
      ) : null}
    </li>
  );
}

const fixtures = <code className="font-mono text-sm">backend/tests/fixtures/</code>;

/** Each source, in the dropdown's order, and what its capture takes. */
const SOURCES: Record<CaptureSource, { label: string; description: (timeZone: string) => ReactNode }> = {
  skyswitch: {
    label: "SkySwitch",
    description: (timeZone) => (
      <>
        After the test calls, choose the window they happened in (your time, {timeZone}). The
        worker reads those calls from SkySwitch and from what it pushed, and the phone schedule
        (each extension&apos;s answering rules and their time frames), sanitizes them the same way,
        and checks the result. Then download the zip and unzip it into {fixtures} on a new branch.
      </>
    ),
  },
  // ScreenConnect's pushed host connections (roadmap PR-37b; ADR 0069).
  screenconnect: {
    label: "ScreenConnect",
    description: (timeZone) => (
      <>
        After the test sessions, choose the window they happened in (your time, {timeZone}). The
        app takes the connects and disconnects ScreenConnect&apos;s automation sent in that
        window, sanitizes them, and checks the result. Then download the zip and unzip it into{" "}
        {fixtures} on a new branch.
      </>
    ),
  },
  // The RESTful API Manager's answers for the sessions the automation pushed
  // (roadmap PR-41b; ADR 0072): read by the worker, the only container with its
  // secret.
  screenconnect_api: {
    label: "ScreenConnect API",
    description: (timeZone) => (
      <>
        After a session, choose a window it&apos;s in (your time, {timeZone}). The worker asks
        ScreenConnect&apos;s RESTful API Manager about up to 5 sessions the automation pushed in
        that window, and for the sessions active in the last day. It sanitizes the answers, and
        lists each field&apos;s type and the counts, without their values.
        It needs the worker&apos;s ScreenConnect API values (screenconnect.md step 7). Then
        download the zip and unzip it into {fixtures} on a new branch.
      </>
    ),
  },
  // Datto RMM's activity log for a window (roadmap PR-47; ADR 0080): read by
  // the worker, the only container with Datto's API user.
  datto_rmm: {
    label: "Datto RMM",
    description: (timeZone) => (
      <>
        After the test day&apos;s web remote session, quick job and component run, choose the window
        they happened in (your time, {timeZone}). The worker reads Datto&apos;s activity log for that
        window, with the sites and users, and sanitizes them: Datto&apos;s categories and actions
        are kept, and every name and detail is faked. It needs the worker&apos;s Datto values
        (datto-rmm.md step 4). Then download the zip and unzip it into {fixtures} on a new branch.
      </>
    ),
  },
  // One tech's Inbox and Sent Items for a window (roadmap PR-56): read by the
  // worker, the only container with Graph's certificate.
  msgraph: {
    label: "Microsoft 365",
    description: (timeZone) => (
      <>
        After the test emails, choose the tech and the window they were sent and received in (your
        time, {timeZone}). The worker reads that window of the tech&apos;s Inbox and Sent Items:
        who, when, the subject and the thread, never a body. It fakes every address, name, subject
        and ID, keeping whether an address is Axon&apos;s, a client&apos;s or public, and a
        subject&apos;s length. It needs the worker&apos;s Microsoft 365 values (m365.md step 6).
        Then download the zip and unzip it into {fixtures} on a new branch.
      </>
    ),
  },
  // What the office SonicWall sends the syslog receiver (ADR 0158 decision 8):
  // recorded from now, for the minutes chosen or until stopped (the owner,
  // 2026-10-16); built by the worker once over.
  sonicwall: {
    label: "SonicWall",
    description: () => (
      <>
        Records what the office SonicWall sends, for the minutes you choose from when you start, or
        until you stop it.
        Meanwhile, from a tech&apos;s office computer, open a client firewall&apos;s admin page, an
        RDP session and an SSH session, and note when. Once it&apos;s over, the worker sanitizes the
        messages (every address faked, as office or internet, every name redacted) and deletes what
        was recorded. It needs the syslog receiver set up (sonicwall.md steps 1 and 2). Then
        download the zip and unzip it into {fixtures} on a new branch.
      </>
    ),
  },
};

// What the office SonicWall sends, recorded from now (ADR 0158 decision 8).
const RECORD_MINUTES = ["5", "10", "15"];

function mailboxLabel(mailbox: CaptureMailbox): string {
  return mailbox.display_name ? `${mailbox.display_name} (${mailbox.email})` : mailbox.email;
}

const SOURCE_ORDER = Object.keys(SOURCES) as CaptureSource[];

/**
 * One panel for every source: the dropdown chooses the source, and the same
 * window, Prepare capture, list, Download and Delete serve each. Its panel is
 * Owner tools' Captures (ADR 0198): a tool, not a status, so not on Health.
 */
export function CaptureTools() {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const sourceId = useId();
  const startId = useId();
  const endId = useId();
  const techId = useId();
  const [source, setSource] = useState<CaptureSource>("skyswitch");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  // Microsoft 365's only: whose mail to read.
  const [tech, setTech] = useState("");
  // The SonicWall's only: how long to record, from now.
  const [minutes, setMinutes] = useState("10");
  const minutesId = useId();
  const recording = source === "sonicwall";
  const [formError, setFormError] = useState<string | null>(null);

  const captures = useQuery({
    // Each panel its own list: another source's captures never push this
    // one's out (Idea #308). Invalidating QUERY_KEY reaches every panel's.
    queryKey: [...QUERY_KEY, source],
    queryFn: () => api.listCaptures(source),
    // Poll while the worker is still preparing one.
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.status === "pending") ? 3000 : false,
    retry: (failures, error) =>
      !(error instanceof ApiError && error.status < 500) && failures < 2,
  });
  // Asked for once Microsoft 365 is chosen: the techs whose mail is read.
  const mailboxes = useQuery({
    queryKey: MAILBOXES_KEY,
    queryFn: () => api.listCaptureMailboxes(),
    enabled: source === "msgraph",
    retry: (failures, error) =>
      !(error instanceof ApiError && error.status < 500) && failures < 2,
  });
  const prepare = useMutation({
    mutationFn: (chosen: CaptureSource) => {
      if (chosen === "sonicwall") {
        // From now: the server starts it when it's asked for, whatever this clock says.
        const now = new Date();
        return api.requestCapture(
          now.toISOString(),
          new Date(now.getTime() + Number(minutes) * 60_000).toISOString(),
          chosen,
        );
      }
      return api.requestCapture(
        zonedToUtc(start, me.timezone).toISOString(),
        zonedToUtc(end, me.timezone).toISOString(),
        chosen,
        chosen === "msgraph" ? tech : undefined,
      );
    },
    onSuccess: () => {
      setFormError(null);
      return queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => {
      setFormError(errorMessage(e));
    },
  });

  // The chosen source's own captures (Idea #308).
  const mine = (captures.data ?? []).filter((c) => c.source === source);

  return (
    <>
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (source === "msgraph" && !tech) {
            setFormError("Choose the tech whose mail to capture.");
            return;
          }
          if (!recording && (!start || !end)) {
            setFormError("Choose when the window starts and ends.");
            return;
          }
          prepare.mutate(source);
        }}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={sourceId} className="text-sm font-medium">
            Source
          </label>
          <select
            id={sourceId}
            className={`${inputClass} w-fit`}
            value={source}
            onChange={(event) => {
              setSource(event.target.value as CaptureSource);
              setFormError(null);
            }}
          >
            {SOURCE_ORDER.map((s) => (
              <option key={s} value={s}>
                {SOURCES[s].label}
              </option>
            ))}
          </select>
        </div>
        <p className="text-muted">{SOURCES[source].description(me.timezone)}</p>
        {source === "msgraph" ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={techId} className="text-sm font-medium">
              Tech
            </label>
            {mailboxes.error ? (
              <p className="text-muted">
                The techs could not be loaded. Reload the page to try again.
              </p>
            ) : !mailboxes.data ? (
              <p role="status" className="text-muted">
                Loading…
              </p>
            ) : mailboxes.data.length === 0 ? (
              <p className="text-muted">
                No tech&apos;s mail is read yet: turn on Read mail for one on Mappings → Techs.
              </p>
            ) : (
              <select
                id={techId}
                className={`${inputClass} w-fit`}
                value={tech}
                onChange={(event) => {
                  setTech(event.target.value);
                  setFormError(null);
                }}
              >
                <option value="">Choose a tech</option>
                {mailboxes.data.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {mailboxLabel(m)}
                  </option>
                ))}
              </select>
            )}
          </div>
        ) : null}
        <div className="flex flex-wrap items-end gap-3">
          {recording ? (
            <div className="flex flex-col gap-1">
              <label htmlFor={minutesId} className="text-sm font-medium">
                Minutes to record
              </label>
              <select
                id={minutesId}
                className={`${inputClass} w-fit`}
                value={minutes}
                onChange={(event) => {
                  setMinutes(event.target.value);
                }}
              >
                {RECORD_MINUTES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <label htmlFor={startId} className="text-sm font-medium">
                  From
                </label>
                <input
                  id={startId}
                  type="datetime-local"
                  className={inputClass}
                  value={start}
                  onChange={(event) => {
                    setStart(event.target.value);
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={endId} className="text-sm font-medium">
                  To
                </label>
                <input
                  id={endId}
                  type="datetime-local"
                  className={inputClass}
                  value={end}
                  onChange={(event) => {
                    setEnd(event.target.value);
                  }}
                />
              </div>
            </>
          )}
          <button
            type="submit"
            disabled={prepare.isPending}
            className="h-10 cursor-pointer rounded-pill bg-cta-bg px-5 font-semibold text-cta-fg hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {recording ? "Start recording" : "Prepare capture"}
          </button>
        </div>
      </form>
      {formError !== null ? (
        <p role="alert" className="mt-2 text-status-needs-client-fg">
          {formError}
        </p>
      ) : null}
      <div className="mt-5">
        {captures.error ? (
          <p className="text-muted">
            The captures could not be loaded. Reload the page to try again.
          </p>
        ) : !captures.data ? (
          <p role="status" className="text-muted">
            Loading…
          </p>
        ) : mine.length === 0 ? (
          <p className="text-muted">{`No ${SOURCES[source].label} captures yet.`}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {mine.map((capture) => (
              <CaptureRow
                key={capture.id}
                capture={capture}
                timeZone={me.timezone}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
